import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { User } from '../types/user';

// Types

interface Session {
  token: string;
  refreshToken: string;
  expiresAt: string;
  context: 'admin' | 'staff' | 'client';
}

interface AuthState {
  user: User | null;
  session: Session | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

interface AuthContextType extends AuthState {
  login: (email: string, password: string, context?: string) => Promise<{ success: boolean; message: string }>;
  logout: () => Promise<void>;
  switchContext: (context: 'admin' | 'staff' | 'client') => Promise<{ success: boolean; message: string }>;
  hasRole: (roles: string | string[]) => boolean;
  hasPermission: (permission: string) => boolean;
  refreshUser: () => Promise<void>;
}

// Initial state
const initialState: AuthState = {
  user: null,
  session: null,
  isAuthenticated: false,
  isLoading: true,
  error: null
};

// Action types
type AuthAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'LOGIN_SUCCESS'; payload: { user: User; session: Session } }
  | { type: 'LOGOUT' }
  | { type: 'SWITCH_CONTEXT'; payload: string }
  | { type: 'REFRESH_USER'; payload: User };

// Reducer
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, isLoading: false };
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        session: action.payload.session,
        isAuthenticated: true,
        isLoading: false,
        error: null
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        session: null,
        isAuthenticated: false,
        isLoading: false,
        error: null
      };
    case 'SWITCH_CONTEXT':
      return {
        ...state,
        session: state.session ? { ...state.session, context: action.payload as any } : null
      };
    case 'REFRESH_USER':
      return {
        ...state,
        user: action.payload,
        isLoading: false
      };
    default:
      return state;
  }
};

// Create context
const AuthContext = createContext<AuthContextType | null>(null);

// API helper functions
const apiRequest = async (endpoint: string, options: RequestInit = {}) => {
  const token = localStorage.getItem('auth_token');
  
  const response = await fetch(`/api${endpoint}`, {
    headers: {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...options.headers
    },
    ...options
  });

  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.error || data.message || 'Request failed');
  }
  
  return data;
};

// Auth Provider Component
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Initialize auth state on app load
  useEffect(() => {
    let isValidating = false; // Prevent concurrent validation requests
    
    const initializeAuth = async () => {
      if (isValidating) {
        // Skip if validation already in progress
        return;
      }
      
      isValidating = true;
      
      const token = localStorage.getItem('auth_token');
      const refreshToken = localStorage.getItem('refresh_token');
      const storedContext = localStorage.getItem('auth_context');
      
      
      if (!token) {
        dispatch({ type: 'SET_LOADING', payload: false });
        isValidating = false;
        return;
      }

      try {
        const response = await apiRequest('/auth/validate-session', {
          method: 'GET'
        });

        if (response.success) {
          dispatch({
            type: 'LOGIN_SUCCESS',
            payload: {
              user: response.user,
              session: {
                token,
                refreshToken: refreshToken || '',
                expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days from now
                context: storedContext as any || 'client' // Use stored context or default to client
              }
            }
          });
        } else {
          localStorage.removeItem('auth_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('auth_context');
          dispatch({ type: 'SET_LOADING', payload: false });
        }
      } catch (error) {
        console.error('❌ Auth initialization error:', error);
        localStorage.removeItem('auth_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('auth_context');
        dispatch({ type: 'SET_LOADING', payload: false });
      } finally {
        isValidating = false;
      }
    };

    initializeAuth();
  }, []);

  // Login function
  const login = async (email: string, password: string, context?: string) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null });

    try {
      const response = await apiRequest('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password, context })
      });

      if (response.success) {
        // Store tokens and context
        localStorage.setItem('auth_token', response.token);
        localStorage.setItem('auth_context', context || 'client');
        
        dispatch({
          type: 'LOGIN_SUCCESS',
          payload: {
            user: response.user,
            session: {
              token: response.token,
              refreshToken: '', // Backend doesn't provide refresh token yet
              expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days from now
              context: context || 'client' // Use the provided context, default to client
            }
          }
        });

        return { success: true, message: 'Login successful' };
      } else {
        dispatch({ type: 'SET_ERROR', payload: response.error });
        return { success: false, message: response.error };
      }
    } catch (error: any) {
      const message = error.message || 'Login failed';
      dispatch({ type: 'SET_ERROR', payload: message });
      return { success: false, message };
    }
  };

  // Logout function
  const logout = async () => {
    try {
      await apiRequest('/auth/logout', { method: 'POST' });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('auth_context');
      sessionStorage.clear(); // Clear modal flags to allow re-display on next login
      dispatch({ type: 'LOGOUT' });
    }
  };

  // Switch context function
  const switchContext = async (context: 'admin' | 'staff' | 'client') => {
    try {
      const response = await apiRequest('/auth/switch-context', {
        method: 'POST',
        body: JSON.stringify({ context })
      });

      if (response.success) {
        localStorage.setItem('auth_context', context);
        dispatch({ type: 'SWITCH_CONTEXT', payload: context });
        return { success: true, message: response.message || `Switched to ${context} context` };
      } else {
        return { success: false, message: response.error || 'Context switch failed' };
      }
    } catch (error: any) {
      return { success: false, message: error.message || 'Context switch failed' };
    }
  };

  // Check if user has specific role(s)
  const hasRole = (roles: string | string[]) => {
    if (!state.user) return false;
    
    const roleArray = Array.isArray(roles) ? roles : [roles];
    return roleArray.some(roleToCheck => state.user!.role === roleToCheck);
  };

  // Check if user has specific permission
  const hasPermission = (permission: string) => {
    if (!state.user) return false;
    
    // Basic permission mapping based on roles
    const rolePermissions: Record<string, string[]> = {
      admin: ['admin', 'staff', 'client', 'create', 'read', 'update', 'delete'],
      staff: ['staff', 'client', 'read', 'update'],
      client: ['client', 'read']
    };

    return rolePermissions[state.user.role]?.includes(permission) || false;
  };

  // Refresh user data
  const refreshUser = async () => {
    if (!state.session?.token) return;

    try {
      const response = await apiRequest('/auth/validate-session', {
        method: 'GET'
      });

      if (response.success) {
        dispatch({ type: 'REFRESH_USER', payload: response.user });
      }
    } catch (error) {
      console.error('User refresh error:', error);
    }
  };

  const contextValue: AuthContextType = {
    ...state,
    login,
    logout,
    switchContext,
    hasRole,
    hasPermission,
    refreshUser
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};